/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { APPOINTMENT_STATUSES } from "../../constant/OttoAnalyticsConstants";

export const CALENDAR_VIEW = "CALENDAR_VIEW";
export const CUSTOM = "CUSTOM";

// Values for the appointment list's "filter by created/due date" toggle.
// Maps 1:1 to the GraphQL `filterDateType` argument.
export const APPOINTMENT_DATE_MODE = {
  CREATED_ON: "CREATED_ON",
  DUE_ON: "DUE_ON",
};

const initialState = {
  filters: {
    userIds: [],
    appointmentStatus: [...APPOINTMENT_STATUSES],
    userMode: 0,
    range: {
      startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
      endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
    },
    page: 1, // do not persist this
    pageSize: 10, // do not persist this
    calendarMode: "dayGridMonth",
    dateMode: APPOINTMENT_DATE_MODE.DUE_ON,
    dateType: CALENDAR_VIEW,
    opportunityTypeId: null,
    customRange: { startDate: null, endDate: null },
    // Carried over from the dashboard "by sales person / by source" tables so
    // the appointment list reproduces the same waterfall vs current-status
    // view the user clicked from. `null` = page applies its own default.
    viewMode: null,
    // Set when the user drills in from the "by source" table — restricts the
    // appointment list to that source. Holds the clicked source + every one
    // of its children (per the dropdown's source tree), so the dropdown's
    // checkboxes mirror the dashboard's drill-in selection 1:1.
    opportunitySourceIds: [],
    // Mirrors the dashboard's "Users / Teams" toggle (`users` | `teams`). When
    // `teams`, the query sends `teamIds` and skips `userIds`/`createdByUserIds`.
    entityMode: "users",
    // The mode the user has actually committed to (made a selection in). The
    // visible toggle is `entityMode`; this trails behind. When they differ,
    // the dropdown is in "preview" — show placeholder, not an "All" chip.
    activeFilterMode: "users",
    teamIds: [],
    // Flipped on by a dashboard drill-in (goToAppointments /
    // goToAppointmentsFromSource) so the appointment page can render a
    // banner explaining the count difference (the dashboard excludes
    // deactivated users; this list does not). The page clears it on unmount.
    cameFromDashboard: false,
  },
};

const appointmentSlice = createSlice({
  name: "appointment",
  initialState,
  reducers: {
    reducerSetAppointmentUserIds(state, action) {
      state.filters.userIds = action.payload;
    },
    reducerSetAppointmentStatus(state, action) {
      state.filters.appointmentStatus = action.payload;
    },
    reducerSetAppointmentRange(state, action) {
      state.filters.range = action.payload;
    },
    reducerSetAppointmentUserMode(state, action) {
      state.filters.userMode = action.payload;
    },
    reducerSetPagination(state, action) {
      state.filters.page = action.payload.page;
      state.filters.pageSize = action.payload.pageSize;
    },
    reducerSetAppointmentCalendarMode(state, action) {
      state.filters.calendarMode = action.payload;
    },
    reducerSetAppointmentDateMode(state, action) {
      state.filters.dateMode = action.payload;
    },
    reducerSetAppointmentDateType(state, action) {
      state.filters.dateType = action.payload;
      // Clear custom range whenever the preset changes, so Custom always starts empty.
      state.filters.customRange = { startDate: null, endDate: null };
    },
    reducerSetAppointmentCustomRange(state, action) {
      state.filters.customRange = action.payload || { startDate: null, endDate: null };
    },
    reducerClearDateFilters(state) {
      state.filters.dateMode = null;
      state.filters.dateType = null;
      state.filters.range = {
        startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
        endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
      };
      state.filters.customRange = { startDate: null, endDate: null };
    },
    reducerSetOpportunityTypeId(state, action) {
      state.filters.opportunityTypeId = action.payload;
    },
    reducerSetAppointmentViewMode(state, action) {
      state.filters.viewMode = action.payload;
    },
    reducerSetAppointmentOpportunitySourceIds(state, action) {
      state.filters.opportunitySourceIds = action.payload || [];
    },
    // Shallow-merge any subset of filters in one dispatch. Used by dashboard
    // drill-in flows (e.g. Appointment-by-Sales-Person, Appointment-by-Source)
    // so the call site passes a single object instead of 8+ individual
    // dispatches. Keys not present in the payload are left untouched.
    reducerSetAppointmentFilters(state, action) {
      Object.assign(state.filters, action.payload || {});
    },
    // Re-apply the default "all statuses applied" list. Used by logout and
    // by "Add Appointment" so a fresh appointment flow starts with the full
    // status set selected. opportunitySlice listens to this same action via
    // extraReducers and clears its Type/Status filters in the same dispatch.
    resetAppointmentFilters(state) {
      state.filters.appointmentStatus = [...APPOINTMENT_STATUSES];
      state.filters.opportunitySourceIds = [];
      state.filters.opportunityTypeId = null;
    },
  },
});

export const {
  reducerSetAppointmentUserIds,
  reducerSetAppointmentRange,
  reducerSetAppointmentStatus,
  reducerSetAppointmentUserMode,
  reducerSetPagination,
  reducerSetAppointmentCalendarMode,
  reducerSetAppointmentDateType,
  reducerSetAppointmentDateMode,
  reducerClearDateFilters,
  reducerSetOpportunityTypeId,
  reducerSetAppointmentCustomRange,
  reducerSetAppointmentViewMode,
  reducerSetAppointmentOpportunitySourceIds,
  reducerSetAppointmentFilters,
  resetAppointmentFilters,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;
